import { ChevronDown, ChevronRight, MessageSquare, MoreHorizontal, Pin, Plus } from "lucide-react";
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";

import type { RoomRailSessionNode } from "~/neucleos/projectRoomRailGroups";
import { isHandSession, type RoomRailCard } from "~/neucleos/projectRoomRailCards";
import { WorkroomIcon, workroomAccentVariable } from "./WorkroomIcon";
import "./RoomCardsRail.css";

/**
 * The Desk's room-card rail (the principal, 16 Oct 2026), after the
 * neucleos-3 cockpit's RoomGroupsRail: one card per Room, General among them,
 * each card's sessions inside it, and a hand's session nested under the
 * session that launched it. Controlled presentation only: the caller owns the
 * World reads, the session births, the menus' operations and preferences.
 */

export type RoomCardAction = "pin" | "unpin" | "move-up" | "move-down" | "hide" | "tidy";
export type RoomCardSessionAction = "rename" | "close" | "archive";

const actionLabels: Record<RoomCardAction | RoomCardSessionAction, string> = {
  pin: "Pin",
  unpin: "Unpin",
  "move-up": "Move up",
  "move-down": "Move down",
  hide: "Hide room",
  tidy: "Tidy up this room…",
  rename: "Rename",
  close: "Close session",
  archive: "Archive",
};

export interface RoomCardsRailProps {
  readonly cards: ReadonlyArray<RoomRailCard>;
  readonly activeSessionId: string | null;
  readonly compact?: boolean;
  readonly emptyLabel?: string;
  /** Hand rows the person opened or folded, by session id; absent = the default. */
  readonly sessionFolds?: Readonly<Record<string, boolean>>;
  readonly onToggleCard: (cardId: string) => void;
  readonly onNewSession: (card: RoomRailCard) => void;
  readonly onSelectSession: (sessionId: string) => void;
  readonly onFoldSession?: (sessionId: string, open: boolean) => void;
  readonly onCardAction?: (cardId: string, action: RoomCardAction) => void;
  readonly onSessionAction?: (sessionId: string, action: RoomCardSessionAction) => void;
  /** A live status mark for a session row (the dock passes its status ring). */
  readonly renderSessionStatus?: (sessionId: string) => ReactNode;
  /** Wraps each card, e.g. in the dock's drag-reorder item. */
  readonly wrapCard?: (card: RoomRailCard, node: ReactNode) => ReactNode;
}

function ActionMenu<Action extends RoomCardAction | RoomCardSessionAction>({
  label,
  actions,
  onAction,
}: {
  label: string;
  actions: readonly Action[];
  onAction: (action: Action) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const choices = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    choices.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const dismiss = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);
  return (
    <div
      className="ncl-room-menu"
      ref={root}
      onKeyDown={(event) => {
        if (open && event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          trigger.current?.focus();
        }
      }}
    >
      <button
        className="ncl-room-icon-button"
        type="button"
        ref={trigger}
        aria-label={label}
        title={label}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(!open)}
      >
        <MoreHorizontal size={15} aria-hidden="true" />
      </button>
      {open && (
        <div
          className="ncl-room-menu-choices"
          id={id}
          ref={choices}
          role="group"
          aria-label={label}
        >
          {actions.map((action) => (
            <button
              type="button"
              key={action}
              className={
                action === "archive" || action === "hide" ? "ncl-room-menu-archive" : undefined
              }
              onClick={() => {
                setOpen(false);
                trigger.current?.focus();
                onAction(action);
              }}
            >
              {actionLabels[action]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function hasAddressedDescendant(node: RoomRailSessionNode): boolean {
  return node.children.some((child) => child.addressed || hasAddressedDescendant(child));
}

function SessionRows({
  nodes,
  parentLabel,
  depth,
  props,
}: {
  nodes: ReadonlyArray<RoomRailSessionNode>;
  parentLabel: string | null;
  depth: number;
  props: RoomCardsRailProps;
}) {
  return (
    <>
      {nodes.map((node) => {
        const { session } = node;
        const focused = session.sessionId === props.activeSessionId;
        const hand = isHandSession(node);
        const hands = node.children.length;
        const open =
          props.sessionFolds?.[session.sessionId] ??
          (node.addressed || hasAddressedDescendant(node));
        const childListId = `ncl-room-hands-${session.sessionId}`;
        return (
          <li
            key={session.sessionId}
            className={`ncl-room-child${focused ? " is-focused" : ""}${hand ? " is-hand" : ""}`}
            data-room-card-session={session.sessionId}
            data-room-card-hand={hand ? "" : undefined}
            data-room-card-depth={depth}
          >
            <div className="ncl-room-child-row">
              {hands > 0 ? (
                <button
                  className="ncl-room-icon-button ncl-room-fold"
                  type="button"
                  aria-expanded={open}
                  aria-controls={childListId}
                  aria-label={`${open ? "Hide" : "Show"} ${hands} hand session${hands === 1 ? "" : "s"} under ${session.label}`}
                  data-room-card-fold={session.sessionId}
                  onClick={() => props.onFoldSession?.(session.sessionId, !open)}
                >
                  {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </button>
              ) : (
                <span className="ncl-room-fold-spacer" aria-hidden="true" />
              )}
              <button
                className="ncl-room-select"
                type="button"
                title={
                  hand && parentLabel !== null
                    ? `${session.label} · spawned by ${parentLabel}`
                    : session.label
                }
                aria-current={focused ? "true" : undefined}
                onClick={() => props.onSelectSession(session.sessionId)}
              >
                {props.renderSessionStatus?.(session.sessionId)}
                {hand && (
                  <span className="ncl-room-hand-mark" data-room-card-hand-mark="">
                    Hand
                  </span>
                )}
                <span className="ncl-room-label">{session.label}</span>
                {hands > 0 && !open && (
                  <span className="ncl-room-group-count" aria-hidden="true">
                    {hands}
                  </span>
                )}
                {focused && (
                  <span className="ncl-room-active">
                    <span aria-hidden="true" />
                    Active
                  </span>
                )}
              </button>
              {props.onSessionAction && (
                <ActionMenu
                  label={`Manage ${session.label}`}
                  actions={["rename", "close", "archive"] as const}
                  onAction={(action) => props.onSessionAction!(session.sessionId, action)}
                />
              )}
            </div>
            {hands > 0 && (
              <ul
                className="ncl-room-children ncl-room-hands"
                id={childListId}
                hidden={!open}
                aria-label={`Hand sessions under ${session.label}`}
              >
                <SessionRows
                  nodes={node.children}
                  parentLabel={session.label}
                  depth={depth + 1}
                  props={props}
                />
              </ul>
            )}
          </li>
        );
      })}
    </>
  );
}

/** A card's accent, from its Workroom (General wears Cleo's identity). */
export function roomCardStyle(card: RoomRailCard): CSSProperties {
  const accent = `var(${card.workroomKey === null ? "--cleo-identity" : workroomAccentVariable(card.workroomKey)})`;
  return { "--ncl-room-group-accent": accent, "--ncl-room-focus": accent } as CSSProperties;
}

/** The card actions a card at `index` in `cards` offers. */
export function roomCardActions(
  cards: ReadonlyArray<RoomRailCard>,
  index: number,
  canHide = true,
  canTidy = false,
): RoomCardAction[] {
  const card = cards[index]!;
  return [
    card.pinned ? "unpin" : "pin",
    ...(index > 0 && cards[index - 1]!.pinned === card.pinned ? (["move-up"] as const) : []),
    ...(index < cards.length - 1 && cards[index + 1]!.pinned === card.pinned
      ? (["move-down"] as const)
      : []),
    ...(canHide && card.kind === "workroom" ? (["hide"] as const) : []),
    ...(canTidy ? (["tidy"] as const) : []),
  ];
}

/**
 * A card's header: the chevron and label fold it (never address a session),
 * the + starts a session in it, the menu arranges it. `newButton` replaces
 * the default + (the dock passes the General room's own birth button).
 */
export function RoomCardHeader({
  card,
  open,
  listId,
  actions,
  onToggle,
  onNew,
  newButton,
  onAction,
  count,
}: {
  readonly card: RoomRailCard;
  readonly open: boolean;
  /**
   * What the folded card counts. The dock passes its live sessions (working,
   * or needing the person), as the cockpit counts active ones; absent, every
   * open session.
   */
  readonly count?: number | undefined;
  readonly listId?: string | undefined;
  readonly actions: ReadonlyArray<RoomCardAction>;
  readonly onToggle: () => void;
  readonly onNew?: (() => void) | undefined;
  readonly newButton?: ReactNode;
  readonly onAction?: ((action: RoomCardAction) => void) | undefined;
}) {
  return (
    <div className="ncl-room-group-header" data-room-card-header={card.id}>
      <button
        className="ncl-room-group-toggle"
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={`${card.label}${card.addressed ? ", contains the open session" : ""}`}
        title={card.label}
        data-room-card-toggle={card.id}
        onClick={onToggle}
      >
        <span className="ncl-room-chevron" aria-hidden="true">
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </span>
        <span className="ncl-room-group-icon" aria-hidden="true">
          {card.workroomKey === null ? (
            <MessageSquare size={15} />
          ) : (
            <WorkroomIcon workroomKey={card.workroomKey} size={15} />
          )}
        </span>
        <span className="ncl-room-label">{card.label}</span>
        {card.pinned && <Pin className="ncl-room-pin" size={11} aria-label="Pinned" />}
        {!open && (count ?? card.sessionCount) > 0 && (
          <span
            className="ncl-room-group-count"
            aria-label={
              count === undefined
                ? `${card.sessionCount} open session${card.sessionCount === 1 ? "" : "s"}`
                : `${count} live session${count === 1 ? "" : "s"}`
            }
            data-room-card-count={card.id}
          >
            {count ?? card.sessionCount}
          </span>
        )}
      </button>
      {newButton ?? (
        <button
          className="ncl-room-icon-button ncl-room-new"
          type="button"
          aria-label={`New session in ${card.label}`}
          title={`New session in ${card.label}`}
          data-room-card-new={card.id}
          onClick={onNew}
        >
          <Plus size={16} aria-hidden="true" />
        </button>
      )}
      {onAction && actions.length > 0 && (
        <ActionMenu label={`Manage ${card.label}`} actions={actions} onAction={onAction} />
      )}
    </div>
  );
}

function Card({
  card,
  index,
  props,
  as: Root,
}: {
  card: RoomRailCard;
  index: number;
  props: RoomCardsRailProps;
  as: "li" | "div";
}) {
  const listId = useId();
  return (
    <Root
      className={`ncl-room-group${card.addressed ? " is-focused" : ""}`}
      style={roomCardStyle(card)}
      data-room-card={card.id}
      data-room-card-kind={card.kind}
      data-workroom-key={card.workroomKey ?? undefined}
      aria-current={card.addressed ? "page" : undefined}
    >
      <RoomCardHeader
        card={card}
        open={!card.collapsed}
        listId={listId}
        actions={roomCardActions(props.cards, index)}
        onToggle={() => props.onToggleCard(card.id)}
        onNew={() => props.onNewSession(card)}
        onAction={props.onCardAction ? (action) => props.onCardAction!(card.id, action) : undefined}
      />
      <ul
        className="ncl-room-children"
        id={listId}
        hidden={card.collapsed}
        aria-label={`${card.label} sessions`}
      >
        {card.sessions.length === 0 ? (
          <li className="ncl-room-empty">No sessions yet</li>
        ) : (
          <SessionRows nodes={card.sessions} parentLabel={null} depth={0} props={props} />
        )}
      </ul>
    </Root>
  );
}

export function RoomCardsRail(props: RoomCardsRailProps) {
  const { cards, compact = false, emptyLabel = "No rooms" } = props;
  return (
    <nav
      className={`ncl-room-rail${compact ? " is-compact" : ""}`}
      aria-label="Rooms"
      data-room-cards-rail=""
    >
      {cards.length === 0 ? (
        <p className="ncl-room-empty">{emptyLabel}</p>
      ) : (
        <ul className="ncl-room-groups">
          {cards.map((card, index) => {
            if (!props.wrapCard)
              return <Card key={card.id} as="li" card={card} index={index} props={props} />;
            return (
              <li key={card.id} className="ncl-room-card-slot">
                {props.wrapCard(card, <Card as="div" card={card} index={index} props={props} />)}
              </li>
            );
          })}
        </ul>
      )}
    </nav>
  );
}
